import React, {useEffect, useRef, useState} from 'react';
import {
    Box,
    CircularProgress,
    IconButton,
    ListItemButton,
    Menu,
    MenuItem,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import HistoryIcon from '@mui/icons-material/History';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import {RagThreadService} from 'crqlar-react-common/dist/services/ai/ragThread.service';
import {useTranslation} from 'react-i18next';
import type {
    RagThreadDTO,
    RagThreadListRequestDTO,
} from 'crqlar-react-common/dist/models/ai/ragThread.model';
import {hasPendingHandoff, hasUnreadTeamReply} from '../assistantThreadState';

// Inline rename editor shown in place of a row; state stays in the parent.
const ConversationRenameRow: React.FC<{
    value: string;
    onChange: (value: string) => void;
    onSave: () => void;
    onCancel: () => void;
    inputRef: React.Ref<HTMLInputElement>;
}> = ({value, onChange, onSave, onCancel, inputRef}) => {
    const {t} = useTranslation();
    const canSave = !!value.trim();
    return (
        <Box
            onClick={event => event.stopPropagation()}
            onMouseDown={event => event.stopPropagation()}
            sx={{
                display: 'grid',
                gridTemplateColumns: '1fr auto',
                alignItems: 'center',
                gap: 0.75,
                border: '1px solid rgba(25, 118, 210, 0.32)',
                borderRadius: 1,
                bgcolor: 'rgba(25, 118, 210, 0.04)',
                px: 1,
                py: 0.75,
                minHeight: 54,
            }}
        >
            <TextField
                inputRef={inputRef}
                value={value}
                size="small"
                variant="outlined"
                inputProps={{maxLength: 80}}
                placeholder={t('Conversation name')}
                onChange={event => onChange(event.target.value)}
                onKeyDown={event => {
                    if (event.key === 'Enter') {
                        event.preventDefault();
                        onSave();
                    }
                    if (event.key === 'Escape') {
                        event.preventDefault();
                        onCancel();
                    }
                }}
                sx={{
                    '& .MuiOutlinedInput-root': {bgcolor: '#fff', borderRadius: 1},
                    '& .MuiInputBase-input': {fontSize: 14, fontWeight: 500, py: 0.9},
                }}
            />

            <Stack direction="row" spacing={0.25}>
                <Tooltip title={t('Save name')}>
                    <span>
                        <IconButton
                            size="small"
                            aria-label={t('Confirm rename')}
                            disabled={!canSave}
                            onClick={onSave}
                            sx={{color: 'primary.main', '&.Mui-disabled': {color: 'text.disabled'}}}
                        >
                            <CheckIcon sx={{fontSize: 18}}/>
                        </IconButton>
                    </span>
                </Tooltip>
                <Tooltip title={t('Cancel')}>
                    <IconButton
                        size="small"
                        aria-label={t('Cancel rename')}
                        onClick={onCancel}
                        sx={{color: 'text.secondary'}}
                    >
                        <CloseIcon sx={{fontSize: 18}}/>
                    </IconButton>
                </Tooltip>
            </Stack>
        </Box>
    );
};

const ConversationListItem: React.FC<{
    conversation: RagThreadDTO;
    isCurrent: boolean;
    isPending: boolean;
    isUnread: boolean;
    isSelecting: boolean;
    disabled: boolean;
    onSelect: () => void;
    onOpenMenu: (anchor: HTMLElement) => void;
}> = ({conversation, isCurrent, isPending, isUnread, isSelecting, disabled, onSelect, onOpenMenu}) => {
    const {t} = useTranslation();

    return (
        <ListItemButton
            onClick={onSelect}
            selected={isCurrent}
            disabled={disabled}
            sx={{borderRadius: 1, px: 1.25, py: 1, alignItems: 'flex-start'}}
        >
            <Stack sx={{minWidth: 0, flex: 1}} spacing={0.25}>
                <Stack direction="row" alignItems="center" spacing={0.75} sx={{minWidth: 0}}>
                    {(isUnread || isPending) && (
                        <Tooltip title={isUnread ? t('The team replied') : t('Waiting on the Crqlar support team')}>
                            <Box
                                aria-label={isUnread ? t('The team replied') : t('Waiting on the Crqlar support team')}
                                sx={{
                                    width: 8,
                                    height: 8,
                                    borderRadius: '50%',
                                    bgcolor: isUnread ? 'error.main' : '#ed6c02',
                                    flexShrink: 0,
                                }}
                            />
                        </Tooltip>
                    )}
                    <Typography
                        sx={{
                            fontSize: 14,
                            fontWeight: 500,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            minWidth: 0,
                        }}
                    >
                        {conversation.name || t('Untitled conversation')}
                    </Typography>
                </Stack>
            </Stack>

            {isSelecting && (
                <CircularProgress size={16} thickness={3} sx={{mt: 0.25, ml: 0.5, animationDuration: '1.8s'}}/>
            )}

            <Tooltip title={t('More')}>
                <IconButton
                    size="small"
                    aria-label={t('Conversation options')}
                    disabled={disabled}
                    onClick={event => {
                        event.preventDefault();
                        event.stopPropagation();
                        onOpenMenu(event.currentTarget);
                    }}
                    onMouseDown={event => event.stopPropagation()}
                    sx={{mt: -0.25, ml: 0.5}}
                >
                    <MoreVertIcon sx={{fontSize: 18}}/>
                </IconButton>
            </Tooltip>
        </ListItemButton>
    );
};

interface Props {
    /** Highlighted in the list if it matches an entry. */
    currentConversationId: string | null;
    /** Called when the user picks a conversation to load. */
    onSelect: (id: string) => void | Promise<void>;
    /** Called when the user clicks the "new conversation" + button. */
    onStartNew: () => void;
    /** Called after a conversation has been deleted. */
    onDeleted?: (id: string) => void;
    /** Called after a conversation has been renamed. */
    onRenamed?: (conversation: RagThreadDTO) => void;
    /** Thread ids the parent knows have a pending handoff, even if the list payload's
     *  handoffStatus doesn't say so — the open conversation is polled every few seconds,
     *  this list only when it is shown. Falls back to the payload-based check for
     *  everything else. */
    extraPendingConversationIds?: readonly string[];
    /** Thread ids found unread by the panel-wide poll after this list mounted. */
    extraUnreadConversationIds?: readonly string[];
}

/**
 * Conversations browser. Self-contained: fetches the list via
 * RagThreadService.listThreads on mount and reports user actions
 * (select / start-new) to the parent — it doesn't know about asking,
 * loading transcripts, or the chat state. Those concerns belong to
 * AssistantChat / useAssistantChat.
 */
const AssistantConversations: React.FC<Props> = ({
    extraPendingConversationIds,
    extraUnreadConversationIds,
    currentConversationId,
    onSelect,
    onStartNew,
    onDeleted,
    onRenamed,
}) => {
    const {t} = useTranslation();
    const [conversations, setConversations] = useState<RagThreadDTO[]>([]);
    const [loading, setLoading] = useState(false);
    const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
    const [menuConversation, setMenuConversation] = useState<RagThreadDTO | null>(null);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [draftName, setDraftName] = useState('');
    const [selectingId, setSelectingId] = useState<string | null>(null);
    const renameInputRef = useRef<HTMLInputElement | null>(null);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        const request: RagThreadListRequestDTO = {limit: {limit: 50, offset: 0}};

        RagThreadService.listThreads(request)
            .then(page => {
                if (cancelled) return;
                setConversations(page.content || []);
            })
            .catch(() => {
                if (cancelled) return;
                setConversations([]);
            })
            .finally(() => {
                if (cancelled) return;
                setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        if (!editingId) return;

        const timer = window.setTimeout(() => {
            renameInputRef.current?.focus({preventScroll: true});
            renameInputRef.current?.select();
        }, 0);

        return () => window.clearTimeout(timer);
    }, [editingId]);

    const closeMenu = () => {
        setMenuAnchor(null);
        setMenuConversation(null);
    };

    const startRename = (conversation: RagThreadDTO) => {
        closeMenu();
        setEditingId(conversation.id || null);
        setDraftName(conversation.name || '');
    };

    const cancelRename = () => {
        setEditingId(null);
        setDraftName('');
    };

    const saveRename = async (conversation: RagThreadDTO) => {
        const conversationId = conversation.id;
        const name = draftName.trim();
        if (!conversationId) return;
        if (!name) {
            cancelRename();
            return;
        }
        if (name === (conversation.name || '').trim()) {
            cancelRename();
            return;
        }

        const previousConversations = conversations;
        const optimisticConversation = {...conversation, name};
        setConversations(curr =>
            curr.map(item => item.id === conversationId ? optimisticConversation : item),
        );
        cancelRename();

        try {
            const saved = await RagThreadService.renameThread(conversationId, {name});
            const renamedConversation = saved || optimisticConversation;
            setConversations(curr =>
                curr.map(item => item.id === conversationId ? renamedConversation : item),
            );
            onRenamed?.(renamedConversation);
        } catch {
            setConversations(previousConversations);
        }
    };

    const deleteConversation = async (conversation: RagThreadDTO) => {
        const conversationId = conversation.id;
        if (!conversationId) return;

        closeMenu();
        const previousConversations = conversations;
        setConversations(curr => curr.filter(item => item.id !== conversationId));
        if (editingId === conversationId) {
            cancelRename();
        }

        try {
            await RagThreadService.deleteThreads({id: [conversationId]});
            onDeleted?.(conversationId);
        } catch {
            setConversations(previousConversations);
        }
    };

    const selectConversation = async (conversationId: string) => {
        if (selectingId) return;
        setSelectingId(conversationId);
        try {
            await onSelect(conversationId);
        } finally {
            setSelectingId(null);
        }
    };

    return (
        <Box sx={{flex: 1, overflowY: 'auto', p: 2}}>
            <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{mb: 1.5}}
            >
                <Typography
                    sx={{
                        fontSize: 13,
                        fontWeight: 600,
                        color: 'text.secondary',
                        textTransform: 'uppercase',
                        letterSpacing: 0.6,
                    }}
                >
                    {t('History')}
                </Typography>
                <Tooltip title={t('New conversation')}>
                    <IconButton
                        size="small"
                        onClick={onStartNew}
                        aria-label={t('Start a new conversation')}
                    >
                        <AddIcon fontSize="small"/>
                    </IconButton>
                </Tooltip>
            </Stack>

            {loading && (
                <Stack alignItems="center" sx={{mt: 4}}>
                    <CircularProgress size={20} thickness={3} sx={{animationDuration: '1.8s'}}/>
                </Stack>
            )}

            {!loading && conversations.length === 0 && (
                <Stack
                    spacing={1}
                    alignItems="center"
                    sx={{mt: 6, color: 'text.secondary'}}
                >
                    <HistoryIcon sx={{fontSize: 32, opacity: 0.4}}/>
                    <Typography sx={{fontSize: 14}} textAlign="center">
                        {t('No previous conversations yet.')}
                    </Typography>
                </Stack>
            )}

            {!loading && conversations.length > 0 && (
                <Stack spacing={0.25}>
                    {conversations.map(conv =>
                        editingId === conv.id ? (
                            <ConversationRenameRow
                                key={conv.id}
                                value={draftName}
                                onChange={setDraftName}
                                onSave={() => saveRename(conv)}
                                onCancel={cancelRename}
                                inputRef={renameInputRef}
                            />
                        ) : (
                            <ConversationListItem
                                key={conv.id}
                                conversation={conv}
                                isCurrent={conv.id === currentConversationId}
                                isPending={hasPendingHandoff(conv) || (!!conv.id && !!extraPendingConversationIds?.includes(conv.id))}
                                isUnread={hasUnreadTeamReply(conv) || (!!conv.id && !!extraUnreadConversationIds?.includes(conv.id))}
                                isSelecting={selectingId === conv.id}
                                disabled={!!selectingId}
                                onSelect={() => conv.id && selectConversation(conv.id)}
                                onOpenMenu={anchor => {
                                    setMenuAnchor(anchor);
                                    setMenuConversation(conv);
                                }}
                            />
                        ),
                    )}
                </Stack>
            )}

            <Menu
                anchorEl={menuAnchor}
                open={!!menuAnchor}
                onClose={closeMenu}
                transformOrigin={{horizontal: 'right', vertical: 'top'}}
                anchorOrigin={{horizontal: 'right', vertical: 'bottom'}}
            >
                <MenuItem
                    disabled={!menuConversation?.id}
                    onClick={() => menuConversation && startRename(menuConversation)}
                    sx={{fontSize: 14}}
                >
                    <EditOutlinedIcon
                        fontSize="small"
                        sx={{mr: 1, color: 'text.secondary'}}
                    />
                    {t('Rename')}
                </MenuItem>
                <MenuItem
                    disabled={!menuConversation?.id}
                    onClick={() => menuConversation && deleteConversation(menuConversation)}
                    sx={{fontSize: 14}}
                >
                    <DeleteOutlineIcon
                        fontSize="small"
                        sx={{mr: 1, color: 'text.secondary'}}
                    />
                    {t('Delete')}
                </MenuItem>
            </Menu>
        </Box>
    );
};

export default AssistantConversations;
